import { Component, inject, signal } from '@angular/core';
import { form, FormField, FormRoot, min, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Currency } from '@app/shared/types/enums/currency.enum';
import { QuantityUnits } from '@app/shared/types/enums/quantity-units.enum';
import { TimeUnits } from '@app/shared/types/enums/time-units.enum';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { TaskContractCost } from '@app/shared/types/models/task/task-contract-cost';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { amountAndUnitToDuration, durationToHours } from '@app/shared/utils/time-utils';
import { TranslatePipe } from '@ngx-translate/core';

interface ContractCostFormModel {
  time: { amount: number | null; units: TimeUnits };
  financial: { amount: number | null; currency: Currency };
  quantity: { amount: number | null; units: QuantityUnits };
}

function buildInitialModel(contractCost?: TaskContractCost): ContractCostFormModel {
  const model: ContractCostFormModel = {
    time: { amount: null, units: TimeUnits.HOURS },
    financial: { amount: null, currency: Currency.NOK },
    quantity: { amount: null, units: QuantityUnits.PIECES }
  };

  if (contractCost?.time) {
    model.time = {
      amount: durationToHours(contractCost.time),
      units: TimeUnits.HOURS
    };
  }

  if (contractCost?.financial?.currency) {
    model.financial = {
      amount: contractCost.financial.amount,
      currency: contractCost.financial.currency
    };
  }

  if (contractCost?.quantity?.unit) {
    model.quantity = {
      amount: contractCost.quantity.amount,
      units: contractCost.quantity.unit
    };
  }

  return model;
}

export interface ContractCostDialogData {
  contractCost?: TaskContractCost;
  autoFocus: boolean;
}

@Component({
  selector: 'proffeo-contract-cost-dialog',
  templateUrl: './contract-cost-dialog.component.html',
  imports: [
    FormField,
    FormRoot,
    TranslatePipe,
    MatDialogModule,
    MatIconModule,
    UiButtonComponent,
    UiInputComponent,
    UiSelectComponent,
    UiDialogComponent
  ]
})
export class ContractCostDialogComponent {
  private readonly dialogRef: MatDialogRef<ContractCostDialogComponent> = inject(
    MatDialogRef<ContractCostDialogComponent>
  );
  private readonly modalData: ContractCostDialogData = inject(MAT_DIALOG_DATA);

  protected readonly contractCostModel = signal<ContractCostFormModel>(buildInitialModel(this.modalData.contractCost));

  protected readonly contractCostForm = form(this.contractCostModel, path => {
    min(path.time.amount, 0);
    min(path.financial.amount, 0);
    min(path.quantity.amount, 0);
  });

  protected readonly timeUnitOptions: DropdownItem<TimeUnits>[] = [
    { label: 'time-units.hours', value: TimeUnits.HOURS },
    { label: 'time-units.minutes', value: TimeUnits.MINUTES },
    { label: 'time-units.man-days', value: TimeUnits.MAN_DAYS }
  ];

  protected readonly currencyOptions: DropdownItem<Currency>[] = [
    { label: 'NOK', value: Currency.NOK },
    { label: 'PLN', value: Currency.PLN },
    { label: 'EUR', value: Currency.EUR },
    { label: 'USD', value: Currency.USD }
  ];

  protected readonly quantityUnitOptions: DropdownItem<QuantityUnits>[] = [
    { label: 'quantity-units.pieces', value: QuantityUnits.PIECES },
    { label: 'quantity-units.meters', value: QuantityUnits.METERS },
    { label: 'quantity-units.meters-square', value: QuantityUnits.METERS_SQUARE },
    { label: 'quantity-units.meters-cubic', value: QuantityUnits.METERS_CUBIC },
    { label: 'quantity-units.sets', value: QuantityUnits.SETS }
  ];

  protected saveContractCost(): void {
    void submit(this.contractCostForm, {
      action: async () => {
        const { amount: timeAmount, units } = this.contractCostModel().time;
        const { amount: costAmount, currency } = this.contractCostModel().financial;
        const { amount: quantityAmount, units: quantityUnits } = this.contractCostModel().quantity;

        const result: TaskContractCost = {
          time: amountAndUnitToDuration(timeAmount, units) ?? null,
          financial: { amount: costAmount, currency },
          quantity: { amount: quantityAmount, unit: quantityUnits }
        };

        this.dialogRef.close(result);
      }
    });
  }

  protected cancelContractCost(): void {
    this.dialogRef.close();
  }
}
